import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { mockApi } from "../../services/mockApi";
import Swal from "sweetalert2";

import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import Header from "../../components/Header";
import styles from "./style.module.css";
import { getFormattedTime } from "../../utilities/axios.utilities";
import ProgressBar from "../NH/components/ProgressBar";

interface TableItem {
  time?: string;
  percent: number;
  typeGame?: string;
  name: string;
  _id: string;
  showIcon?: string;
  vassalage?: string;
  normalizedName?: string;
}

const Slot = () => {
  const MOBILE_BREAKPOINT = 430;
  const MOBILE_INITIAL_VISIBLE = 12;
  const DESKTOP_INITIAL_VISIBLE = 40;
  const MOBILE_STEP_VISIBLE = 12;
  const DESKTOP_STEP_VISIBLE = 40;

  const [isShowLogout, setIsShowLogout] = useState(false);
  const [tableList, setTableList] = useState<TableItem[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
      ? MOBILE_INITIAL_VISIBLE
      : DESKTOP_INITIAL_VISIBLE
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const scrollTickingRef = useRef(false);
  const { room } = useParams();
  const currentTime = getFormattedTime();

  // Bỏ dấu tiếng Việt (kể cả đ, ư, ơ, ă, â, ê, ô) để tìm dễ
  const normalizeString = (str: string) => {
    const s = str.toLowerCase();
    const from = "àáảãạăằắẳẵặâầấẩẫậèéẻẽẹêềếểễệìíỉĩịòóỏõọôồốổỗộơờớởỡợùúủũụưừứửữựỳýỷỹỵđ";
    const to = "aaaaaaaaaaaaaaaaaeeeeeeeeeeeiiiiiooooooooooooooooouuuuuuuuuuuyyyyyd";
    let out = "";
    for (let i = 0; i < s.length; i++) {
      const idx = from.indexOf(s[i]);
      out += idx >= 0 ? to[idx] : s[i];
    }
    return out.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // Search luôn chạy trên toàn bộ dữ liệu API đã tải (tableList), không phụ thuộc visibleCount.
  const filteredTableList = useMemo(() => {
    if (!searchKeyword) return tableList;
    return tableList.filter((item) =>
      (item.normalizedName || normalizeString(item.name)).includes(searchKeyword)
    );
  }, [tableList, searchKeyword]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchKeyword(normalizeString(searchInput.trim()));
      setVisibleCount(isMobile ? MOBILE_INITIAL_VISIBLE : DESKTOP_INITIAL_VISIBLE);
    }, 120);
    return () => clearTimeout(timer);
  }, [searchInput, isMobile]);

  // Loading frame for both mobile and PC
  useEffect(() => {
    
    Swal.fire({
      html: `
        <div style="
          border: 2px solid transparent;
          border-image: linear-gradient(45deg, #8a2be2, #00bfff, #ff0096, #8a2be2) 1;
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          color: white;
          max-width: 350px;
          width: 90%;
          box-shadow: 
            0 0 50px rgba(138, 43, 226, 0.6),
            0 0 100px rgba(0, 191, 255, 0.3),
            0 0 150px rgba(255, 0, 150, 0.2),
            0 20px 60px rgba(0, 0, 0, 0.7),
            inset 0 0 40px rgba(138, 43, 226, 0.15),
            inset 0 0 80px rgba(0, 191, 255, 0.1);
          position: relative;
          font-family: 'Courier New', monospace;
        ">
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 30px;
            animation: textGlow 2s ease-in-out infinite;
          ">
            <div style="
              width: 80px;
              height: 80px;
              background: linear-gradient(135deg, #8a2be2, #00bfff);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 20px;
              font-size: 40px;
              color: #000;
              font-weight: bold;
              box-shadow: 
                0 0 20px rgba(138, 43, 226, 0.6),
                0 0 40px rgba(0, 191, 255, 0.3);
              animation: iconPulse 1.5s ease-in-out infinite;
              border: 2px solid rgba(255, 255, 255, 0.3);
            ">⚡</div>
            <h2 style="
              margin: 0; 
              color: #8a2be2; 
              font-size: 20px; 
              font-weight: 900;
              text-shadow: 
                0 0 15px rgba(138, 43, 226, 0.8),
                0 0 25px rgba(138, 43, 226, 0.5);
              letter-spacing: 2px;
              text-transform: uppercase;
              text-align: center;
              line-height: 1.2;
            ">INITIALIZING HACK TOOL...</h2>
          </div>
          
          <div style="
            margin-bottom: 25px; 
            font-size: 14px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          ">
            <div style="
              padding: 8px 16px;
              background: rgba(255, 255, 255, 0.05);
              border-radius: 6px;
              border: 1px solid rgba(255, 255, 255, 0.1);
              animation: progressGlow 1.5s ease-in-out infinite;
              text-align: center;
            ">
              <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">Đang tải dữ liệu sảnh game...</span>
            </div>
            <div data-progress="2" style="
              padding: 8px 16px;
              background: rgba(255, 255, 255, 0.05);
              border-radius: 6px;
              border: 1px solid rgba(255, 255, 255, 0.1);
              animation: progressGlow 1.5s ease-in-out infinite 0.5s;
              text-align: center;
            ">
              <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">Đang tải danh sách game...</span>
            </div>
          </div>
          
          <div style="
            width: 100%;
            height: 16px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 15px;
            position: relative;
          ">
            <div id="progress-fill" style="
              width: 0%;
              height: 100%;
              background: linear-gradient(90deg, #8a2be2, #00bfff, #ff0096, #8a2be2);
              border-radius: 4px;
              transition: width 0.3s ease;
              box-shadow: 0 0 10px rgba(138, 43, 226, 0.5);
            "></div>
            <div id="progress-text" style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              color: white;
              font-size: 12px;
              font-weight: bold;
              text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
            ">0%</div>
          </div>
          
          <div style="
            font-size: 14px; 
            color: #00bfff;
            text-shadow: 0 0 10px rgba(0, 191, 255, 0.5);
            letter-spacing: 1px;
          ">
            » HACK TOOL READY «
          </div>
        </div>
        
        <style>
          .loading-frame-popup {
            z-index: 99999 !important;
          }
          @keyframes textGlow {
            0%, 100% { text-shadow: 0 0 10px rgba(138, 43, 226, 0.5); }
            50% { text-shadow: 0 0 20px rgba(138, 43, 226, 0.8); }
          }
          @keyframes iconPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          @keyframes progressGlow {
            0%, 100% { 
              background: rgba(255, 255, 255, 0.05);
              border-color: rgba(255, 255, 255, 0.1);
            }
            50% { 
              background: rgba(138, 43, 226, 0.1);
              border-color: rgba(138, 43, 226, 0.3);
            }
          }
          @keyframes progressBar {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        </style>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: 'rgba(0, 0, 0, 0.7)',
      customClass: {
        popup: 'loading-frame-popup'
      },
      backdrop: `
        rgba(0, 0, 0, 0.7)
        url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238a2be2' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
        center/15px
      `
    });

    // Progress bar animation
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 3.34; // 100% in ~3 seconds (update every 100ms)
      if (progress > 100) progress = 100;
      
      const progressFill = document.getElementById('progress-fill');
      const progressText = document.getElementById('progress-text');
      
      if (progressFill && progressText) {
        progressFill.style.width = progress + '%';
        progressText.textContent = Math.round(progress) + '%';
      }
      
      // Show "Đã hack thành công!" when progress reaches 100%
      if (progress >= 100) {
        const lastMessage = document.querySelector('[data-progress="2"]');
        if (lastMessage) {
          lastMessage.innerHTML = '<span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">Đã hack thành công!</span>';
        }
        clearInterval(progressInterval);
      }
    }, 100);

    // Close loading after 3 seconds
    setTimeout(() => {
      clearInterval(progressInterval);
      Swal.close();
      // Keep isLoading as true to show content
    }, 3000);

    let isMounted = true;

    const fetchTableList = async () => {
      try {
        // TẤT CẢ sảnh đều dùng mock API
        if (room) {
          const mockResponse = await mockApi.getTableList(room);
          if (isMounted) {
            const optimizedData = (mockResponse.data || []).map((item: TableItem) => ({
              ...item,
              normalizedName: normalizeString(item.name || ""),
            }));
            setTableList(optimizedData);
          }
        }
      } catch (error) {
        console.error('Error fetching table list:', error);
      }
    };

    fetchTableList();
    window.scrollTo(0, 0);
    setVisibleCount(isMobile ? MOBILE_INITIAL_VISIBLE : DESKTOP_INITIAL_VISIBLE);

    return () => {
      isMounted = false;
    };
  }, [room, isMobile]);

  // Track viewport width to adjust header/search layout only on mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setVisibleCount((prev) =>
      Math.max(prev, isMobile ? MOBILE_INITIAL_VISIBLE : DESKTOP_INITIAL_VISIBLE)
    );
  }, [isMobile]);

  // Infinite scroll: tăng dần số lượng item hiển thị khi scroll gần cuối trang
  useEffect(() => {
    const handleScroll = () => {
      if (scrollTickingRef.current) return;
      scrollTickingRef.current = true;
      requestAnimationFrame(() => {
        const scrollPosition = window.innerHeight + window.scrollY;
        const threshold = document.body.offsetHeight - (isMobile ? 260 : 400);
        if (scrollPosition >= threshold) {
          setVisibleCount((prev) => {
            const maxVisible = filteredTableList.length || tableList.length;
            if (prev >= maxVisible) return prev;
            const step = isMobile ? MOBILE_STEP_VISIBLE : DESKTOP_STEP_VISIBLE;
            return Math.min(prev + step, maxVisible);
          });
        }
        scrollTickingRef.current = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [filteredTableList.length, tableList.length, isMobile]);

  const visibleTableList = useMemo(
    () => filteredTableList.slice(0, visibleCount),
    [filteredTableList, visibleCount]
  );

  useEffect(() => {
    localStorage.setItem("NH_PAGE", String(room));
  }, [room]);

  return (
    <div style={{ position: "relative" }}>
      {/* Video background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
          backgroundSize: "contain",
          backgroundRepeat: "repeat",
          backgroundPosition: "top"
        }}
      >
        <source
          src={isMobile ? "/assets/bg-mb.mov" : "/assets/bg-pc.mov"}
          type="video/mp4"
        />
      </video>
      <Header setIsShowLogout={() => setIsShowLogout(true)} />

      {/* Top bar: nền trong suốt để thấy video nền */}
      <div
        style={{
          borderTop: "none",
          background: "linear-gradient(180deg, rgba(37,0,0,0.55) 0%, rgba(20,0,0,0.7) 100%)",
          padding: "16px 20px 24px",
          marginBottom: 24,
        }}
      >
        {/* Hàng: QUAY LẠI (trái) + Tiêu đề (giữa) */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: isMobile ? "flex-start" : "space-between",
            marginBottom: isMobile ? 16 : 24,
            gap: isMobile ? 8 : 16,
          }}
        >
          <a
            href="/NH"
            style={{
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "#fff",
              fontWeight: 700,
              textDecoration: "none",
              textTransform: "uppercase",
              letterSpacing: 1,
              padding: isMobile ? "6px 10px" : "8px 16px",
              background: "rgba(0,0,0,0.4)",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              alignSelf: isMobile ? "flex-start" : "auto",
              fontSize: isMobile ? 12 : 14,
            }}
          >
            <span style={{ fontSize: 18 }}>‹‹</span>
            <span>QUAY LẠI</span>
          </a>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              minWidth: 0,
              marginTop: isMobile ? 6 : 0,
            }}
          >
            <h1
              style={{
                margin: 0,
                color: "#fff",
                fontSize: isMobile ? "clamp(22px, 7vw, 30px)" : "clamp(28px, 5vw, 48px)",
                fontWeight: 900,
                textShadow: "0 0 20px rgba(255,255,255,0.5), 0 0 40px rgba(255,0,0,0.3)",
                letterSpacing: 2,
                fontFamily: '"Source Code Pro", monospace',
              }}
            >
              SẢNH {room?.toUpperCase() || "PG"}
            </h1>
            <p
              style={{
                margin: "8px 0 0",
                color: "#fff",
                fontSize: isMobile ? "clamp(12px, 3.5vw, 16px)" : "clamp(14px, 2.5vw, 20px)",
                fontWeight: 600,
                letterSpacing: 2,
                textShadow: "0 0 12px rgba(255,0,0,0.4)",
                fontFamily: '"Source Code Pro", monospace',
              }}
            >
              [ CÔNG NGHỆ AI ]
            </p>
          </div>
          {!isMobile && <div style={{ flex: "0 0 auto", width: 100 }} aria-hidden />}
        </div>

        {/* Ô tìm kiếm + nút Tìm ngay */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            flexWrap: isMobile ? "nowrap" : "wrap",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: isMobile ? "flex-start" : "center",
            gap: 12,
            maxWidth: isMobile ? "100%" : 560,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              flex: isMobile ? "1 1 100%" : "1 1 280px",
              minWidth: isMobile ? "100%" : 200,
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 14,
                color: "rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </span>
            <input
              type="search"
              value={searchInput}
              onChange={handleSearch}
              placeholder="Tìm kiếm game..."
              style={{
                width: "100%",
                height: isMobile ? 40 : 44,
                paddingLeft: 42,
                paddingRight: 14,
                color: "#fff",
                background: "rgba(80,0,0,0.7)",
                border: "none",
                borderRadius: 10,
                outline: "none",
                boxShadow: "0 0 8px rgba(255,255,255,0.12)",
                fontFamily: "inherit",
                fontSize: isMobile ? 14 : 15,
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => {}}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
              height: isMobile ? 40 : 44,
              paddingLeft: isMobile ? 16 : 20,
              paddingRight: isMobile ? 16 : 20,
              color: "#fff",
              fontWeight: 700,
              background: "linear-gradient(90deg, #dc2626, #ea580c)",
              border: "none",
              borderRadius: 10,
              cursor: "pointer",
              boxShadow: "0 0 8px rgba(255,255,255,0.16)",
              fontFamily: "inherit",
              fontSize: isMobile ? 14 : 15,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span>Tìm ngay</span>
          </button>
        </div>
      </div>

      <div className="container-fluid lobby-bg position-relative mx-auto mb-5 max-w-screen-xl">
        <div className="container mx-auto">
          <label
            className={
              "font-semibold text-white text-center block mb-4 " + styles.loadTime
            }
          >
            {room} | Load Time: {currentTime}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
            {visibleTableList.length > 0 ? (
              visibleTableList.map((item, index) => (
                <div
                  key={item._id || index}
                  className={
                    isMobile
                      ? ""
                      : "transition-all duration-300 ease-in-out transform hover:scale-105"
                  }
                  style={{
                    opacity: 1,
                    animation: isMobile ? "none" : "fadeIn 0.3s ease-in-out",
                    contentVisibility: "auto",
                    containIntrinsicSize: "154px",
                  }}
                >
                  <ProgressBar
                    percentage={item.percent}
                    title={item.name}
                    imageUrl={
                      item.showIcon ||
                      `/assets/NH/${room}/${room}_${(index + 1)
                        .toString()
                        .padStart(2, "0")}.png`
                    }
                    id={item._id}
                  />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center text-white py-20">
                <div className="text-2xl font-bold mb-4">
                  Không có dữ liệu game
                </div>
                <div className="text-lg opacity-75">
                  Vui lòng thử lại sau hoặc liên hệ admin
                </div>
              </div>
            )}
          </div>
          <div style={{ height: 200 }}></div>
        </div>
      </div>
      <ModalConfirmLogout
        isShowLogout={isShowLogout}
        setIsShowLogout={() => {
          setIsShowLogout(false);
        }}
      />
    </div>
  );
};

export default Slot;
